import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { tap, map, catchError, shareReplay, switchMap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { Router } from "@angular/router";
import { AuthenticationService } from "./authentication.service";
import { radioTechnology, operators, environment } from "../../../environments/environment";
import { getName } from "country-list";
import { CdpConnectionSearchResponse } from "../models/layout/cdp-connection-search/cdp-connection-search-response";
import { CdpConnectionTagCloudResponse } from "../models/layout/cdp-connection-tag-cloud/cdp-connection-tag-cloud-response";
import { CdpConnectionAggregateArrayResponse } from "../models/layout/cdp-connection-aggregate/cdp-connection-aggregate-array/cdp-connection-aggregate-response";
import { CdpConnectionAggregateObjectResponse } from "../models/layout/cdp-connection-aggregate/cdp-connection-aggregate-object/cdp-connection-aggregate-response";
import { CdpConnectionAggregateNumberResponse } from "../models/layout/cdp-connection-aggregate/cdp-connection-aggregate-number/cdp-connection-aggregate-response";
import { CdpConnectionAggregateLocationsResponse } from "../models/layout/cdp-connection-aggregate-locations/cdp-connection-aggregate-locations-response";
import { LogListResponse } from "../models/layout/log-list/log-list-response";
import { CdpConnectionFindResponse } from "../models/layout/connection-find/connection-find-response";
import { Lwm2mDeviceFindResponse } from "../models/layout/lwm2m-device-find/lwm2m-device-find-response";
import { CdpResolveMccmncResponse } from "../models/layout/cdp-resolve-mccmnc/cdp-resolve-mccmnc-response";
import { PropertyHistoryResponse } from "../models/layout/property-history/property-history-response";
import { AttributeHistoryResponse } from "../models/layout/attribute-history/attribute-history-response";
import { LocationDecodeResponse } from "../models/layout/location-decode/location-decode-response";
import { LocationNetworkDecodeResponse } from "../models/layout/location-network-decode/location-network-decode-response";
import { ThingListResponse } from "../models/layout/thing-list/thing-list-response";
import { SessionInfoResponse, SESSION_INFO_COMMAND } from "../models/layout/session/info/SessionInfo";

@Injectable({
    providedIn: "root"
})
export class ApiService {
    execEndpoint = this.authenticationService.execEndpoint;
    appCenterUrl = this.authenticationService.appCenterUrl;
    globalSessionId: string = this.authenticationService.globalSessionId;

    private refreshTrigger = new BehaviorSubject<void>(undefined);
    public sessionApi: Observable<SessionInfoResponse> = this.refreshTrigger.pipe(
        // request to session api, using share replay to chache the result utitll the refreshSessionInfo is exetuted
        switchMap(() => {
           return this.executeCommand<SessionInfoResponse>(SESSION_INFO_COMMAND)
        }),
        shareReplay(1),
    );

    // Force refresh of sessionApi
    private refreshSessionInfo() {
        this.refreshTrigger.next();
    }

    constructor(
        private authenticationService: AuthenticationService,
        private cookieService: CookieService,
        private http: HttpClient,
        private router: Router,
    ) { }

    parseColors(name, type) {
        const env = type === "Radio technology" ? radioTechnology : operators;
        const keys = Object.keys(env);
        const values = Object.values(env);
        let color = "";

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];

            if (typeof name !== "undefined" && name.includes(key)) {
                color = `#${values[i]}`;
            }
        }
        return color;
    }

    getData(cmd, params) {
        return this.authenticationService
            .execObservable(cmd, params, "all")
            .pipe(
                tap((response: any) => {
                    this.handleResponse(response, cmd)
                    return response;
                }),
                catchError((err) => this.handleError(err)))
            .pipe(
                map((data) => {
                    if (data["cmd"] !== undefined && data["cmd"]["success"]) {
                        return data["cmd"]["params"];
                    }
                    return data["cmd"];
                })
            );

    }

    getConnectionFind(command: string, params: any): Observable<any> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";


        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<any>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: any) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }
    getCdpEuiccFind(command: string, params: any): Observable<any> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<any>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: any) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }
    getCdpEuiccMnoList(command: string, params: any): Observable<any> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<any>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: any) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }
    getCdpConnectionSearchInfo(command: string, params: object): Observable<CdpConnectionSearchResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionSearchResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionSearchResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getCdpConnectionTagCloudList(command: string, params: object): Observable<CdpConnectionTagCloudResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionTagCloudResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionTagCloudResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getCdpConnectionAggregateArrayEntities(command: string, params: object): Observable<CdpConnectionAggregateArrayResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionAggregateArrayResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionAggregateArrayResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getCdpConnectionAggregateObjectEntities(command: string, params: object): Observable<CdpConnectionAggregateObjectResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionAggregateObjectResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionAggregateObjectResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getThingAggregateNumberEntity(command: string, params: object): Observable<CdpConnectionAggregateNumberResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionAggregateNumberResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionAggregateNumberResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getCdpConnectionAggregateLocations(command: string, params: object): Observable<CdpConnectionAggregateLocationsResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionAggregateLocationsResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionAggregateLocationsResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getEventLogList(command: string, params: object, commandType: string): Observable<LogListResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<LogListResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: LogListResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getCdpConnectionFind(command: string, params: object): Observable<CdpConnectionFindResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpConnectionFindResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpConnectionFindResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getLwm2mDeviceFind(command: string, params: object): Observable<Lwm2mDeviceFindResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<Lwm2mDeviceFindResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: Lwm2mDeviceFindResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getCdpResolveMccmnc(command: string, params: object): Observable<CdpResolveMccmncResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<CdpResolveMccmncResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: CdpResolveMccmncResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getPropertyHistory(command: string, params: object, commandType: string): Observable<PropertyHistoryResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<PropertyHistoryResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: PropertyHistoryResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getAttributeHistory(command: string, params: object): Observable<AttributeHistoryResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<AttributeHistoryResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: AttributeHistoryResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));;
    }

    getLocationDecode(command: string, params: object): Observable<LocationDecodeResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<LocationDecodeResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: LocationDecodeResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getLocationNetworkDecode(command: string, params: object): Observable<LocationNetworkDecodeResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<LocationNetworkDecodeResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: LocationNetworkDecodeResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));
    }

    getThingList(command: string, params: object, commandType: string): Observable<ThingListResponse> {
        const data: object = {};
        const cmdObj: object = {};

        let header = "cmd";

        data["command"] = command;
        data["params"] = params;

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }
        if (command === "api.authenticate") {
            header = "auth";
        }

        cmdObj[header] = data;

        return this.http
            .post<ThingListResponse>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: ThingListResponse) => {
                    this.handleResponse(response, command)
                    return response;
                }),
                catchError((err) => this.handleError(err)));

    }

    getRssiColor(rssi) {
        let color = "green";
        if (rssi <= -115) {
            color = "red";
        } else if (rssi > -115 && rssi <= -105) {
            color = "orange";
        } else if (rssi > -105 && rssi <= -85) {
            color = "yellow";
        } else if (rssi === 0) {
            color = "grey";
        }
        return color;
    }

    getPercentColor(pct) {
        let color = "green";
        if (pct <= 20) {
            color = "red";
        } else if (pct > 20 && pct <= 40) {
            color = "orange";
        } else if (pct > 40 && pct <= 60) {
            color = "yellow";
        }
        return color;
    }

    getPercentileIcon(rank) {
        let picon = "fa-grin";
        let pcolor = "green";
        if (rank >= 75) {
            picon = "fa-grin";
        } else if (rank >= 50 && rank < 75) {
            picon = "fa-smile";
        } else if (rank >= 40 && rank < 50) {
            picon = "fa-meh";
            pcolor = "orange";
        } else if (rank >= 20 && rank < 40) {
            picon = "fa-frown";
            pcolor = "orange";
        } else if (rank >= 10 && rank < 20) {
            picon = "fa-angry";
            pcolor = "red";
        } else {
            picon = "fa-dizzy";
            pcolor = "red";
        }

        rank = rank.toFixed(0);
        let rankStr = "";
        const j = rank % 10,
            k = rank % 100;
        if (j === 1 && k !== 11) {
            rankStr = rank + "st";
        } else if (j === 2 && k !== 12) {
            rankStr = rank + "nd";
        } else if (j === 3 && k !== 13) {
            rankStr = rank + "rd";
        } else {
            rankStr = rank + "th";
        }

        return { rankStr: rankStr, color: pcolor, icon: picon };
    }

    convertCountryCode(code) {
        switch (code) {
            case "kr":
                return "South Korea";
            case "ru":
                return "Russia";
            case "us":
                return "USA";
            case "gb":
                return "United Kingdom";
            default:
                return getName(code);
        }
    }

    /**
     * Function to execute an http requet to server
     * all call all made by post
     * if present an app-center-sessionId will be added to the http request
     * if envriroment useMock is set to true data from mock (if present) will be used instead of executing the http request
     *
     * @param command command of api call
     * @param params params object passed to api call
     * @returns response of api call
     */
    get<T, S = unknown>(command: string, params: S = null): Observable<T> {
        const cmdObj: { cmd: object, auth?: object } = {
            cmd: { command, params }
        };

        if (
            this.cookieService.check("app-center-sessionId") &&
            this.cookieService.get("app-center-sessionId") !== ""
        ) {
            cmdObj["auth"] = {
                sessionId: this.globalSessionId
            };
        }

        return this.http
            .post<T>(this.execEndpoint, cmdObj, {})
            .pipe(
                tap((response: T) => {
                    this.handleResponse(response, command);
                }),
                catchError((err) => this.handleError(err)));
    }


    /**
     * Function to execute an http requet to server
     * all call all made by post
     * if present an app-center-sessionId will be added to the http request
     * if envriroment useMock is set to true data from mock (if present) will be used instead of executing the http request
     *
     * @param command command of api call
     * @param params params object passed to api call
     * @returns response of api call
     */
    executeCommand<T extends BaseResponseType, S = unknown>(command: string, params?: S): Observable<T> {
        let payload: BaseRequestType<S> = {
            cmd: {
                command,
                params
            }
        }
        return this.executeMultipleCommandRequest<BaseRequestType<S>, T>(payload)
    }

    executeMultipleCommandRequest<T, R extends { [key in keyof T]: any }>(data): Observable<R> {
        let payload = { ...data };
        if (this.authenticationService?.globalSessionId) {
            payload = { ...payload, auth: { sessionId: this.authenticationService.globalSessionId } }
        }
        return this.http.post<R>(this.authenticationService.execEndpoint, payload);
    }

    handleResponse(response, command) {
        // if success is false and command is not session.info refresh the session to check it is still open
        if (response["success"] === false && command != SESSION_INFO_COMMAND) {
            this.refreshSessionInfo();
        }
    }

    handleError(err) {
        return of(err);
    }

}

export type BaseResponseType = {
    cmd: unknown
};

export type BaseRequestType<S> = {
    cmd: {
        command: string,
        params: S
    }
}
